import { boundedPromise, withDeadline } from "@-/dev.improve.haniverse.lib.deadline";

export interface PickedActorDataFile {
  fileName: string;
  json: string;
}

/**
 * A person choosing a file. Generous, because they may be browsing folders, but finite: a picker
 * whose outcome never reaches the page must not leave the Import button spinning.
 */
const FILE_PICK_DEADLINE_MS = 10 * 60_000;

/**
 * Browsers without the input `cancel` event report a cancelled picker only by the page regaining
 * focus with no `change`. A chosen file's `change` follows the focus return almost at once (the
 * file is read later), so this much silence after focus returns means the picker was cancelled.
 */
const PICKER_FOCUS_RETURN_GRACE_MS = 2000;

/** Reading one local JSON export into memory: a disk read of a few megabytes at most. */
const FILE_READ_DEADLINE_MS = 60_000;

export async function shareActorDataJson(json: string, fileName: string): Promise<void> {
  const url = URL.createObjectURL(new Blob([json], { type: "application/json" }));
  const anchor = document.createElement("a");
  anchor.download = fileName;
  anchor.href = url;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export async function pickActorDataJson(): Promise<PickedActorDataFile | null> {
  const file = await boundedPromise<File | null>(
    { deadline: FILE_PICK_DEADLINE_MS, operation: "choose a data file" },
    (resolve) => {
      const input = document.createElement("input");
      input.accept = "application/json,.json";
      input.type = "file";
      let focusReturnTimer: ReturnType<typeof setTimeout> | undefined;
      const onFocusReturn = () => {
        clearTimeout(focusReturnTimer);
        focusReturnTimer = setTimeout(resolve, PICKER_FOCUS_RETURN_GRACE_MS, null);
      };
      input.addEventListener("change", () => resolve(input.files?.item(0) ?? null), {
        once: true,
      });
      input.addEventListener("cancel", () => resolve(null), { once: true });
      window.addEventListener("focus", onFocusReturn);
      input.click();
      return () => {
        clearTimeout(focusReturnTimer);
        window.removeEventListener("focus", onFocusReturn);
      };
    },
  );
  if (file === null) {
    return null;
  }
  // A failed read rejects the import (and clears its spinner) instead of being dropped.
  const json = await withDeadline(
    { deadline: FILE_READ_DEADLINE_MS, operation: "read data file" },
    () => file.text(),
  );
  return { fileName: file.name, json };
}

export async function confirmActorDataReplacement(
  fileName: string,
  rowCount: number,
): Promise<boolean> {
  return globalThis.confirm(
    `${fileName} contains ${rowCount.toLocaleString()} data records.\n\nImporting replaces all Han data currently in this browser and cannot be undone.`,
  );
}
